import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { approvals as approvalsApi } from '../../api/endpoints';
import type { ApprovalView } from '../../api/endpoints';
import { getTokens } from '../../theme/tokens';
import { PromptDialog } from '../../components/ui';

// A strip at the top of every module: what in this module is waiting for
// approval (deletes, new records, and for Budget its proposals), at which
// level, and — for whoever may act — Approve / Reject right here. It shows
// nothing when nothing waits. The full history is in the Approvals inbox.

const when = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
};

const ModuleApprovals = ({ moduleId, isDarkMode }: { moduleId: string; isDarkMode?: boolean }) => {
  const t = getTokens(Boolean(isDarkMode));
  const navigate = useNavigate();
  const [items, setItems] = useState<ApprovalView[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<ApprovalView | null>(null);
  const [approving, setApproving] = useState<ApprovalView | null>(null);

  const load = useCallback(() => {
    approvalsApi.list('module', undefined, moduleId)
      .then((list) => setItems(Array.isArray(list) ? list : []))
      .catch(() => setItems([]));
  }, [moduleId]);

  useEffect(() => {
    load();
    window.addEventListener('approvals-changed', load);
    window.addEventListener('approval-notice', load);
    return () => {
      window.removeEventListener('approvals-changed', load);
      window.removeEventListener('approval-notice', load);
    };
  }, [load]);

  if (!items.length) return null;

  // The outcome goes to the app-wide notice: this strip may disappear (nothing
  // left waiting) the moment the decision is made.
  const tell = (text: string) => window.dispatchEvent(new CustomEvent('approval-info', { detail: text }));

  const act = async (a: ApprovalView, approve: boolean, text = '') => {
    setBusy(a.id);
    setMessage(null);
    try {
      // This list may be out of date (the record was edited, someone else
      // acted): decide on the request as it is now.
      const now = await approvalsApi.get(a.id);
      if (now.status !== 'PENDING' || !now.canAct) {
        setMessage(now.status !== 'PENDING'
          ? `${a.typeLabel}: already ${now.status.toLowerCase()}.`
          : `${a.typeLabel}: ${now.whyNot || 'you cannot act on this now.'}`);
        load();
        return;
      }
      const done = approve
        ? await approvalsApi.approve(now.id, now.version, text || undefined)
        : await approvalsApi.reject(now.id, now.version, text);
      tell(done.status === 'APPROVED' ? `${a.typeLabel} ${a.reference || ''}: approved and carried out.`
        : done.status === 'REJECTED' ? `${a.typeLabel} ${a.reference || ''}: rejected.`
          : `${a.typeLabel} ${a.reference || ''}: your level is approved; now waiting for level ${done.nextLevel}.`);
      window.dispatchEvent(new Event('approvals-changed'));
      // Carried out (or put back): the module's own list must show it now.
      if (done.status !== 'PENDING') window.dispatchEvent(new Event('approvals-applied'));
      load();
    } catch (err) {
      setMessage((err as Error).message || 'Could not complete that.');
      load();
    } finally {
      setBusy(null);
    }
  };

  const mine = items.filter((a) => a.canAct).length;
  const button = (bg: string, fg: string): React.CSSProperties => ({
    border: `1px solid ${bg}`, background: bg, color: fg, borderRadius: t.radius.sm,
    padding: '3px 10px', cursor: 'pointer', fontWeight: 'bold', fontSize: t.fontSize.sm
  });

  return (
    <div style={{
      margin: '12px 16px 0', border: `1px solid ${t.warning}`, borderRadius: t.radius.lg,
      backgroundColor: t.bgSubtle, color: t.textMain, fontSize: t.fontSize.sm
    }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer',
          padding: '10px 14px', color: t.textMain, fontSize: t.fontSize.md, display: 'flex', gap: '10px', alignItems: 'center'
        }}
      >
        <span>🕒</span>
        <span style={{ flex: 1 }}>
          <strong>{items.length}</strong> item{items.length > 1 ? 's' : ''} in this module waiting for approval
          {mine > 0 && <> — <strong style={{ color: t.primaryText }}>{mine} for you</strong></>}
        </span>
        <span style={{ color: t.textMuted }}>{open ? '▲ Hide' : '▼ Show'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 12px' }}>
          {message && <div style={{ margin: '0 0 8px', color: t.textMuted, fontStyle: 'italic' }}>{message}</div>}
          {items.map((a) => (
            <div key={a.id} style={{
              display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap',
              padding: '8px 0', borderTop: `1px solid ${t.border}`
            }}>
              <span style={{ minWidth: '150px', fontWeight: 'bold' }}>{a.typeLabel}</span>
              <span style={{ fontFamily: 'monospace' }}>{a.reference}</span>
              <span style={{ flex: 1, minWidth: '180px', color: t.textMuted }}>{a.summary}</span>
              <span style={{ color: t.warning, whiteSpace: 'nowrap' }}>
                Level {a.nextLevel} of {a.levelsRequired} · {a.waitingFor.join(' or ')}
              </span>
              <span style={{ color: t.textFaint, whiteSpace: 'nowrap' }}>{a.requestedBy} · {when(a.requestedAt)}</span>
              {a.canAct ? (
                <span style={{ display: 'flex', gap: '6px' }}>
                  <button type="button" disabled={busy === a.id} style={button(t.success, '#FFF')} onClick={() => setApproving(a)}>✓ Approve</button>
                  <button type="button" disabled={busy === a.id} style={button(t.danger, '#FFF')} onClick={() => setRejecting(a)}>✕ Reject</button>
                  <button type="button" style={button('transparent', t.primaryText)} onClick={() => navigate('/approvals', { state: { openId: a.id } })}>Details</button>
                </span>
              ) : (
                <span style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {a.whyNot && <span style={{ color: t.textMuted, fontStyle: 'italic' }}>ℹ {a.whyNot}</span>}
                  <button type="button" style={button('transparent', t.primaryText)} onClick={() => navigate('/approvals', { state: { openId: a.id } })}>Details</button>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      <PromptDialog
        open={Boolean(approving)}
        title={approving ? `Approve ${approving.typeLabel} ${approving.reference || ''}` : 'Approve'}
        message="Add a remark if you like. It is kept in the history with your approval."
        placeholder="Remark (optional)"
        confirmLabel="✓ Approve"
        required={false}
        destructive={false}
        onSubmit={(remark: string) => { const a = approving; setApproving(null); if (a) act(a, true, remark); }}
        onCancel={() => setApproving(null)}
        isDarkMode={isDarkMode}
      />
      <PromptDialog
        open={Boolean(rejecting)}
        title="Reject"
        message="Give a reason. The request stops here and the person who asked sees why."
        placeholder="Reason"
        confirmLabel="Reject"
        required
        requiredError="Enter a reason."
        multiline
        destructive
        onSubmit={(reason: string) => { const a = rejecting; setRejecting(null); if (a) act(a, false, reason); }}
        onCancel={() => setRejecting(null)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default ModuleApprovals;
